/**
 * UpcomingMeetings - displays upcoming meetings
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { formatDistanceToNow } from '@/i18n/dateFns';
import type { Meeting } from '@services/api';

interface UpcomingMeetingsProps {
  meetings: Meeting[];
  isLoading?: boolean;
}

const statusColors: Record<string, string> = {
  scheduled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
  in_progress: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
  completed: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200',
  cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
};

export function UpcomingMeetings({ meetings, isLoading }: UpcomingMeetingsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.upcomingMeetings')}</h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
              <div className="h-4 bg-neutral-200 dark:bg-neutral-600 rounded w-3/4 mb-2" />
              <div className="h-3 bg-neutral-200 dark:bg-neutral-600 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{t('dashboard.upcomingMeetings')}</h3>
        <button
          onClick={() => navigate('/meetings')}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {t('dashboard.viewAll')}
        </button>
      </div>
      {meetings.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-neutral-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.noUpcomingMeetings')}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {meetings.map((meeting) => {
            const statusClass = statusColors[meeting.status] ?? statusColors.scheduled;

            return (
              <li
                key={meeting.id}
                onClick={() => navigate(`/meetings/${meeting.id}`)}
                className="p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-600 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {meeting.title}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      {meeting.startTime ? (
                        <>
                          {format(new Date(meeting.startTime), 'EEE, MMM d, yyyy · h:mm a')}
                          <span className="text-neutral-400 dark:text-neutral-500 mx-1">·</span>
                          {formatDistanceToNow(new Date(meeting.startTime), {
                            addSuffix: true,
                          })}
                        </>
                      ) : (
                        <span className="italic">{t('dashboard.noDateScheduled')}</span>
                      )}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${statusClass}`}
                  >
                    {meeting.status.replace('_', ' ')}
                  </span>
                </div>
                {meeting.transcriptStatus === 'completed' && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t('dashboard.transcriptAvailable')}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
